import Environment from '../config/environment';

export interface League {
  id: number;
  name: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface LeagueListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: League[];
}

export interface LeaguePlayer {
  league: number;
  player: number;
  name: string;
  rating: number;
  matches_played: number;
  matches_won: number;
  matches_lost: number;
  matches_drawn: number;
  last_tendency: number;
  league_points: number;
}

export interface LeaguePlayersResponse {
  count: number;
  next?: string;
  previous?: string;
  results: LeaguePlayer[];
}

class LeagueService {
  private baseURL: string;

  constructor() {
    this.baseURL = Environment.fullApiUrl;
  }

  /**
   * Get all leagues
   */
  async getLeagues(): Promise<League[]> {
    try {
      const response = await fetch(`${this.baseURL}/leagues/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data: LeagueListResponse = await response.json();
      return data.results;
    } catch (error) {
      console.error('Error fetching leagues:', error);
      throw error;
    }
  }

  /**
   * Get active leagues only
   */
  async getActiveLeagues(): Promise<League[]> {
    try {
      const response = await fetch(`${this.baseURL}/leagues/?is_active=true`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data: LeagueListResponse = await response.json();
      return data.results;
    } catch (error) {
      console.error('Error fetching active leagues:', error);
      throw error;
    }
  }

  /**
   * Get a specific league by ID
   */
  async getLeague(leagueId: number): Promise<League> {
    try {
      const response = await fetch(`${this.baseURL}/leagues/${leagueId}/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching league:', error);
      throw error;
    }
  }

  /**
   * Get players/ranking for a specific league
   */
  async getLeaguePlayers(leagueId: number): Promise<LeaguePlayer[]> {
    try {
      const response = await fetch(`${this.baseURL}/leagues/${leagueId}/players/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data: LeaguePlayersResponse = await response.json();
      return data.results;
    } catch (error) {
      console.error('Error fetching league players:', error);
      throw error;
    }
  }
}

const leagueService = new LeagueService();
export default leagueService;
